import {
	DateField as AriaDateField,
	type DateFieldProps as AriaDateFieldProps,
	DateInput as AriaDateInput,
	type DateInputProps,
	DateSegment,
	type DateValue,
	composeRenderProps
} from 'react-aria-components';
import { cn } from '../../../components/utils';
import type { BaseFieldProps } from '../../../components/types';
import { FieldDescription } from '../base-controls/FieldDescription';
import { FieldLabelWithHtml } from '../base-controls/FieldLabelWithHtml';
import { FieldError } from '../base-controls/FieldError';
import { FieldGroup } from '../base-controls/FieldGroup';
import { baseControlStyles } from '../base-controls/util';

export interface DateFieldProps<T extends DateValue> extends AriaDateFieldProps<T>, BaseFieldProps {
	dateInputProps?: DateInputProps;
}

export function DateField<T extends DateValue>({ label, description, dateInputProps, ...props }: DateFieldProps<T>) {
	const { base } = baseControlStyles();
	return (
		<AriaDateField
			{...props}
			className={composeRenderProps(props.className, className => cn(base(), className))}
			isRequired={props.required}
			aria-label={label}
		>
			{!props.hideLabel ? <FieldLabelWithHtml className="mb-1" label={label} required={props.required} /> : null}
			<FieldGroup>
				<DateInput {...dateInputProps} isReadOnly={props.isReadOnly} />
			</FieldGroup>

			{description && <FieldDescription>{description}</FieldDescription>}
			{props.error && <FieldError {...props.error} />}
		</AriaDateField>
	);
}

export function DateInput({ className: additionalClassName, ...props }: Omit<DateInputProps, 'children'> & { isReadOnly?: boolean }) {
	return (
		<AriaDateInput
			className={renderProps =>
				cn(
					'block min-h-[40px] w-full rounded-md bg-white px-4 py-2.5 text-sm text-gray-900',
					renderProps.isDisabled && 'bg-gray-50 text-gray-500',
					props.isReadOnly && 'bg-gray-50 forced-colors:bg-[Canvas]',
					additionalClassName
				)
			}
			{...props}
		>
			{segment => (
				<DateSegment
					segment={segment}
					className={renderProps =>
						cn(
							'inline rounded p-0.5 text-gray-800 caret-transparent',
							renderProps.isPlaceholder && 'italic text-gray-500',
							renderProps.isDisabled && 'text-gray-300',
							renderProps.isFocused && 'bg-primary-500 text-white'
						)
					}
				/>
			)}
		</AriaDateInput>
	);
}
